import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CampaignUpdateSchema,
  type CampaignAssigneeKind,
  type CampaignChannel,
} from '@xsc/shared';
import { api, ApiError } from '../lib/api-client.js';
import { fieldErrors } from '../lib/form-helpers.js';
import { Button } from '../components/ui/button.js';
import { Card, CardBody, CardHeader, CardTitle } from '../components/ui/card.js';
import { Checkbox, Input, Select } from '../components/ui/input.js';
import { FormBanner, FormField } from '../components/ui/form.js';
import { AssigneePicker } from '../components/assignee-picker.js';

const ALL_CHANNELS: { value: CampaignChannel; label: string }[] = [
  { value: 'auto_dialer', label: 'Auto-dialer' },
  { value: 'ai_message', label: 'AI message (TTS)' },
  { value: 'sms', label: 'SMS' },
  { value: 'email', label: 'Email' },
];

interface FormState {
  tag: string;
  start_at: string;
  message_template_id: string;
  channels: CampaignChannel[];
  assignee_kind: '' | CampaignAssigneeKind;
  assignee_value: string;
}

const EMPTY: FormState = {
  tag: '',
  start_at: '',
  message_template_id: '',
  channels: [],
  assignee_kind: '',
  assignee_value: '',
};

interface CampaignDetail {
  id: string;
  tag: string;
  start_at: string;
  channels: CampaignChannel[];
  message_template_id?: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed';
  assignee_kind?: CampaignAssigneeKind | null;
  assignee_value?: string | null;
}

interface ListResp<T> { data: T[]; pagination: unknown }
interface MessageTemplateLite { id: string; name: string }

export function EditCampaignPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [form, setForm] = useState<FormState>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string> | null>(null);
  const [bannerError, setBannerError] = useState<string | null>(null);

  const campaignQ = useQuery({
    queryKey: ['campaign', id],
    queryFn: () => api.get(`/v1/campaigns/${id}`) as Promise<CampaignDetail>,
    enabled: !!id,
  });

  const templatesQ = useQuery({
    queryKey: ['message-templates'],
    queryFn: () => api.get('/v1/message-templates') as Promise<ListResp<MessageTemplateLite>>,
  });

  useEffect(() => {
    if (!campaignQ.data) return;
    const c = campaignQ.data;
    setForm({
      tag: c.tag ?? '',
      start_at: isoToLocalInput(c.start_at),
      message_template_id: c.message_template_id ?? '',
      channels: c.channels ?? [],
      assignee_kind: (c.assignee_kind ?? '') as '' | CampaignAssigneeKind,
      assignee_value: c.assignee_value ?? '',
    });
  }, [campaignQ.data]);

  const save = useMutation({
    mutationFn: (payload: unknown) => api.patch(`/v1/campaigns/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      qc.invalidateQueries({ queryKey: ['campaign', id] });
      navigate('/campaigns');
    },
    onError: (err) => setBannerError(extractDetail(err)),
  });

  const update = <K extends keyof FormState>(key: K) => (value: FormState[K]): void => {
    setForm((f) => ({ ...f, [key]: value }));
  };
  const toggleChannel = (c: CampaignChannel): void => {
    setForm((f) => ({
      ...f,
      channels: f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c],
    }));
  };

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (save.isPending) return; // double-save guard
    setBannerError(null);

    const payload: Record<string, unknown> = {
      tag: form.tag.trim(),
      start_at: form.start_at ? new Date(form.start_at).toISOString() : '',
      channels: form.channels,
    };
    if (form.message_template_id) payload['message_template_id'] = form.message_template_id;
    // Assignee: send the pair, OR explicit nulls to clear.
    if (form.assignee_kind && form.assignee_value.trim()) {
      payload['assignee_kind'] = form.assignee_kind;
      payload['assignee_value'] = form.assignee_value.trim();
    } else if (campaignQ.data?.assignee_kind || campaignQ.data?.assignee_value) {
      payload['assignee_kind'] = null;
      payload['assignee_value'] = null;
    }

    const fieldIssues = fieldErrors(CampaignUpdateSchema, payload);
    if (fieldIssues) {
      setErrors(fieldIssues);
      return;
    }
    setErrors(null);
    save.mutate(payload);
  };

  const templates = useMemo(() => templatesQ.data?.data ?? [], [templatesQ.data]);

  if (campaignQ.isLoading) return <p className="text-sm text-ink-muted">Loading…</p>;
  if (campaignQ.error) return <FormBanner message={extractDetail(campaignQ.error)} />;

  // Server enforces 409 if status != pending; surface that up-front.
  const locked = campaignQ.data && campaignQ.data.status !== 'pending';

  return (
    <div className="max-w-2xl space-y-4">
      <header>
        <h1 className="text-2xl font-semibold">Edit campaign</h1>
        <p className="text-sm text-ink-muted">
          Editable while pending. Once the campaign moves to <code>in_progress</code>, only
          status changes are accepted.
        </p>
      </header>

      {locked && (
        <FormBanner
          message={`This campaign is no longer editable (status: ${campaignQ.data!.status}). Save will fail with 409.`}
        />
      )}

      <Card>
        <CardHeader>
          <CardTitle>Campaign</CardTitle>
        </CardHeader>
        <CardBody className="space-y-4">
          {bannerError && <FormBanner message={bannerError} />}

          <form onSubmit={onSubmit} className="space-y-4">
            <FormField label="Tag" required hint="Display label for this campaign" error={errors?.['tag']}>
              {(id) => (
                <Input id={id} value={form.tag} onChange={(e) => update('tag')(e.target.value)} required autoFocus disabled={locked} />
              )}
            </FormField>

            <FormField label="Start" required error={errors?.['start_at']}>
              {(id) => (
                <Input
                  id={id}
                  type="datetime-local"
                  value={form.start_at}
                  onChange={(e) => update('start_at')(e.target.value)}
                  required
                  disabled={locked}
                />
              )}
            </FormField>

            <fieldset className="rounded border border-border p-4 space-y-2">
              <legend className="px-1 text-sm font-medium text-ink-muted">Channels</legend>
              {errors?.['channels'] && <p className="text-xs text-danger">{errors['channels']}</p>}
              <div className="grid grid-cols-2 gap-2">
                {ALL_CHANNELS.map((c) => (
                  <label key={c.value} className="flex items-center gap-2 text-sm">
                    <Checkbox
                      checked={form.channels.includes(c.value)}
                      onChange={() => toggleChannel(c.value)}
                      disabled={locked}
                    />
                    {c.label}
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="rounded border border-border p-4 space-y-3">
              <legend className="px-1 text-sm font-medium text-ink-muted">Assigned to</legend>
              <p className="text-xs text-ink-muted">
                Auto-dialer target — ring this extension or call-queue first then bridge
                to the external party. Leave as <em>None</em> for AI/TTS direct campaigns.
              </p>
              <div className="grid grid-cols-[10rem_1fr] gap-3">
                <Select
                  value={form.assignee_kind}
                  onChange={(e) =>
                    update('assignee_kind')(e.target.value as '' | CampaignAssigneeKind)
                  }
                  disabled={locked}
                >
                  <option value="">— None (AI/TTS) —</option>
                  <option value="extension">Extension</option>
                  <option value="callgroup">Call queue</option>
                </Select>
                <AssigneePicker
                  kind={form.assignee_kind}
                  value={form.assignee_value}
                  onChange={(v) => update('assignee_value')(v)}
                  disabled={locked}
                />
              </div>
              {errors?.['assignee_value'] && (
                <p className="text-xs text-danger">{errors['assignee_value']}</p>
              )}
            </fieldset>

            <FormField label="Message template" error={errors?.['message_template_id']}>
              {(id) => (
                <Select
                  id={id}
                  value={form.message_template_id}
                  onChange={(e) => update('message_template_id')(e.target.value)}
                  disabled={locked}
                >
                  <option value="">— None —</option>
                  {templates.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </Select>
              )}
            </FormField>

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="secondary" onClick={() => navigate('/campaigns')}>
                Cancel
              </Button>
              <Button type="submit" disabled={save.isPending || locked}>
                {save.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}

function isoToLocalInput(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function extractDetail(err: unknown): string {
  if (err instanceof ApiError && typeof err.body === 'object' && err.body && 'detail' in err.body) {
    return String((err.body as { detail: unknown }).detail);
  }
  return (err as Error).message;
}
